import type { TFunction } from 'i18next';
import type { JSX, ReactNode } from 'react';
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IBusinessProfile, IReceiptSettings } from '@feathr/blackbox';
import { showError, toast } from '@feathr/components';
import { useAccount } from '@feathr/extender/state';

import type { IOrganization } from './EmailConfiguratorIframe/EmailConfiguratorIframe.utils';
import { toLegacyAddress } from './EmailConfiguratorIframe/EmailConfiguratorIframe.utils';

function isBusinessProfileComplete(profile: IBusinessProfile | undefined): boolean {
  if (!profile?.tax_id || !profile?.company_address) {
    return false;
  }
  const { line1, city, state, postal_code, country } = profile.company_address;
  return Boolean(line1 && city && state && postal_code && country);
}

function isReceiptConfigValid(
  businessProfile: IBusinessProfile | undefined,
  receiptSettings: IReceiptSettings | undefined,
): boolean {
  return isBusinessProfileComplete(businessProfile) && Boolean(receiptSettings?.from_email);
}

function getMissingSetupFields(
  profile: IBusinessProfile | undefined,
  receiptSettings: IReceiptSettings | undefined,
  t: TFunction,
): string[] {
  const missing: string[] = [];
  if (!profile?.tax_id) {
    missing.push(t('Tax ID / EIN'));
  }
  const addr = profile?.company_address;
  if (!addr?.line1) {
    missing.push(t('Street address'));
  }
  if (!addr?.city) {
    missing.push(t('City'));
  }
  if (!addr?.state) {
    missing.push(t('State'));
  }
  if (!addr?.postal_code) {
    missing.push(t('ZIP code'));
  }
  if (!addr?.country) {
    missing.push(t('Country'));
  }
  if (!receiptSettings?.from_email) {
    missing.push(t('Sender email'));
  }
  return missing;
}

interface IEmailConfiguratorContext {
  editorConfig: IReceiptSettings | null;
  editorOrganization: IOrganization | null;
  editorIsDirty: boolean;
  initialConfig: IReceiptSettings;
  missingSetupFields: string[];
  setEditorConfig: (config: IReceiptSettings, isDirty: boolean) => void;
  setEditorOrganization: (org: IOrganization, isDirty?: boolean) => void;
  handleSave: () => Promise<void>;
  isDirty: boolean;
  lastSaved: string | undefined;
  setupJustCompleted: boolean;
  showConfirmButton: boolean;
}

const EmailConfiguratorContext = createContext<IEmailConfiguratorContext | undefined>(undefined);

interface IProviderProps {
  children: ReactNode;
}

export function EmailConfiguratorProvider({ children }: Readonly<IProviderProps>): JSX.Element {
  const { t } = useTranslation();
  const account = useAccount();

  const initialConfig = account.get('receipt_settings') ?? {};

  const [editorConfigState, setEditorConfigState] = useState<IReceiptSettings | null>(null);
  const [editorOrganization, setEditorOrganizationState] = useState<IOrganization | null>(null);
  const [editorIsDirty, setEditorIsDirty] = useState(false);
  const [setupJustCompleted, setSetupJustCompleted] = useState(false);
  const [missingSetupFields, setMissingSetupFields] = useState<string[]>(() =>
    getMissingSetupFields(account.get('business_profile'), initialConfig, t),
  );

  const lastSaved = account.get('date_last_modified');

  const wasInitiallyComplete = useMemo(
    () => isReceiptConfigValid(account.get('business_profile'), initialConfig),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only compute once on mount
    [],
  );

  const needsConfirmation = useMemo(
    () => wasInitiallyComplete && !initialConfig.setup_completed,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only compute once on mount
    [],
  );

  const hasEditedInSession = useRef(false);

  const setEditorConfig = useCallback(
    (config: IReceiptSettings, isDirtyFromEditor: boolean): void => {
      setEditorConfigState(config);
      if (isDirtyFromEditor) {
        hasEditedInSession.current = true;
      }
      setEditorIsDirty(isDirtyFromEditor);
    },
    [],
  );

  const setEditorOrganization = useCallback((org: IOrganization, markDirty = true): void => {
    setEditorOrganizationState(org);
    if (markDirty) {
      hasEditedInSession.current = true;
      setEditorIsDirty(true);
    }
  }, []);

  const isDirty = editorIsDirty;
  const showConfirmButton = needsConfirmation && !hasEditedInSession.current;

  const handleSave = useCallback(async (): Promise<void> => {
    const configToSave = editorConfigState ?? initialConfig;
    if (configToSave) {
      try {
        const existingProfile = account.get('business_profile') ?? {};

        const updatedBusinessProfile = editorOrganization
          ? {
              ...existingProfile,
              tax_id: editorOrganization.taxId ?? '',
              contact_email: editorOrganization.contactEmail ?? '',
              contact_phone: editorOrganization.contactPhone ?? '',
              company_address: {
                ...existingProfile.company_address,
                ...toLegacyAddress(editorOrganization.address),
              },
            }
          : existingProfile;

        const willBeValid = isReceiptConfigValid(updatedBusinessProfile, configToSave);

        account.set({
          receipt_settings: {
            ...configToSave,
            setup_completed: willBeValid,
          },
        });

        if (editorOrganization) {
          account.set({
            business_profile: updatedBusinessProfile,
          });
        }

        await account.patchDirty();
        hasEditedInSession.current = true;
        setEditorIsDirty(false);
        setMissingSetupFields(getMissingSetupFields(updatedBusinessProfile, configToSave, t));
        if (willBeValid) {
          setSetupJustCompleted(true);
        }
        toast(t('Changes saved.'), { type: 'success' });
      } catch (error) {
        showError(error, t);
      }
    }
  }, [account, editorConfigState, editorOrganization, initialConfig, t]);

  const value = useMemo(
    () => ({
      editorConfig: editorConfigState,
      editorOrganization,
      editorIsDirty,
      initialConfig,
      missingSetupFields,
      setEditorConfig,
      setEditorOrganization,
      handleSave,
      isDirty,
      lastSaved,
      setupJustCompleted,
      showConfirmButton,
    }),
    [
      editorConfigState,
      editorOrganization,
      editorIsDirty,
      initialConfig,
      missingSetupFields,
      setEditorConfig,
      setEditorOrganization,
      handleSave,
      isDirty,
      lastSaved,
      setupJustCompleted,
      showConfirmButton,
    ],
  );

  return (
    <EmailConfiguratorContext.Provider value={value}>{children}</EmailConfiguratorContext.Provider>
  );
}

export function useEmailConfigurator(): IEmailConfiguratorContext {
  const context = useContext(EmailConfiguratorContext);
  if (context === undefined) {
    throw new Error('useEmailConfigurator must be used within EmailConfiguratorProvider');
  }
  return context;
}
